import React from 'react';
import { usersApi, type UserDevicesInfo } from '../../../api/users';
import {
  EmptyState,
  Muted,
  RowCard,
  SectionTitle,
  StatusPill,
  TabLoader,
  formatDateTime,
  useUserTabData,
} from './userTabShared';

interface Props {
  userId: string;
  isDark: boolean;
}

const PLATFORM_LABEL: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web' };

export function UserDevicesTab({ userId, isDark }: Props) {
  const { data, loading } = useUserTabData<UserDevicesInfo>(
    userId,
    usersApi.getUserDevices,
    'Не удалось загрузить устройства',
  );

  if (loading) return <TabLoader />;
  if (!data) return <EmptyState isDark={isDark}>Нет данных.</EmptyState>;

  return (
    <div className="space-y-6">
      <div>
        <SectionTitle isDark={isDark}>Сессии входа</SectionTitle>
        {data.sessions.length === 0 ? (
          <EmptyState isDark={isDark}>Сессий нет.</EmptyState>
        ) : (
          <div className="space-y-2">
            {data.sessions.map((s) => (
              <RowCard key={s.id} isDark={isDark} className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-0.5">
                  <div className="text-sm font-semibold">
                    {s.deviceModel || s.deviceName || 'Устройство'}
                    <span className="font-normal">
                      {' · '}
                      {PLATFORM_LABEL[s.platform] ?? s.platform}
                      {s.osVersion && ` ${s.osVersion}`}
                    </span>
                  </div>
                  <Muted isDark={isDark} className="block">
                    Приложение {s.appVersion ?? '—'}
                    {s.ipAddress && ` · IP ${s.ipAddress}`}
                    {(s.city || s.country) && ` · ${[s.city, s.country].filter(Boolean).join(', ')}`}
                  </Muted>
                  <Muted isDark={isDark} className="block">
                    Вход {formatDateTime(s.createdAt)} · последняя активность {formatDateTime(s.lastActiveAt)}
                  </Muted>
                </div>
                <StatusPill tone={s.isActive ? 'green' : 'slate'}>{s.isActive ? 'Активна' : 'Закрыта'}</StatusPill>
              </RowCard>
            ))}
          </div>
        )}
      </div>

      <div>
        <SectionTitle isDark={isDark}>Push-уведомления</SectionTitle>
        {data.pushDevices.length === 0 ? (
          <EmptyState isDark={isDark}>Устройство для push не зарегистрировано.</EmptyState>
        ) : (
          <div className="space-y-2">
            {data.pushDevices.map((d) => (
              <RowCard key={d.id} isDark={isDark} className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">
                    {PLATFORM_LABEL[d.deviceType ?? ''] ?? d.deviceType ?? 'Устройство'}
                    <span className="font-normal"> · приложение {d.appVersion ?? '—'}</span>
                  </div>
                  <Muted isDark={isDark}>
                    Последний раз {formatDateTime(d.lastUsedAt)}
                    {d.failedAttempts > 0 && ` · неудачных отправок ${d.failedAttempts}`}
                  </Muted>
                </div>
                <StatusPill tone={d.isActive && d.hasPushToken ? 'green' : 'slate'}>
                  {d.isActive && d.hasPushToken ? 'Получает push' : 'Не получает'}
                </StatusPill>
              </RowCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
